// JSON client for the /research/discuss endpoints (PostsController,
// CommentsController). The posts URL doubles as the create URL — GET reads
// the feed, POST makes a post — and the comment URL is derived from it.
// Posts and comments are write-only once made: there is deliberately no
// delete here, and no endpoint behind it.
//
// Pictures live in pictures/api.ts — they're not a discussion concept.
import type { PictureRecord } from "../pictures/api"

export type DiscussUser = {
  id: string
  name: string | null
  username: string | null
  // Always set by the server — the uploaded avatar or the anon placeholder.
  avatar_url: string | null
}

export type CommentRecord = {
  id: string
  post_id: string
  text_contents: string | null
  reply_to_comment_id: string | null
  created_at: string
  is_own: boolean
  commenter: DiscussUser | null
}

// One choice in the report modal's category picker. Server-supplied
// (ModerationReport::REASON_LABELS) so the form can't offer a category the
// model would reject.
export type ReportReason = {
  value: string
  label: string
}

export type ProblemStub = {
  id: string
  title: string
  path?: string
}

export type TagStub = {
  slug: string
  display_name: string
}

export type PostRecord = {
  id: string
  post_type: "text" | "image"
  text_contents: string | null
  // Server-rendered markdown (hashtags already linkified); safe for innerHTML.
  text_html: string
  posted_at: string | null
  created_at: string
  is_own: boolean
  user: DiscussUser | null
  tags: TagStub[]
  pictures: PictureRecord[]
  comments?: CommentRecord[]
  problem?: ProblemStub
}

function csrfToken(): string | undefined {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
}

function jsonHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    "Accept": "application/json",
    "X-CSRF-Token": csrfToken() ?? "",
  }
}

export async function fetchFeed(
  feedUrl: string,
  options: { page?: number; tags?: string[]; problem?: string } = {},
): Promise<PostRecord[] | null> {
  const params = new URLSearchParams()
  if (options.page && options.page > 1) params.set("page", String(options.page))
  if (options.tags && options.tags.length > 0) params.set("tags", options.tags.join(","))
  if (options.problem) params.set("problem", options.problem)
  const query = params.toString()

  try {
    const response = await fetch(query ? `${feedUrl}?${query}` : feedUrl, {
      headers: { "Accept": "application/json" },
    })
    if (!response.ok) return null
    const body = (await response.json()) as { posts: PostRecord[] }
    return body.posts
  } catch {
    return null
  }
}

// Returns the created post, or a string error to show inline.
export async function createPost(
  postsUrl: string,
  post: {
    text_contents: string
    post_type?: "text" | "image"
    picture_ids?: string[]
    problem_id?: string
    tag_slugs?: string[]
  },
): Promise<PostRecord | string> {
  if (!csrfToken()) return "Not signed in."

  try {
    const response = await fetch(postsUrl, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({ post }),
    })
    const payload = (await response.json()) as { post?: PostRecord; error?: string }
    if (!response.ok || !payload.post) return payload.error ?? "Something went wrong."
    return payload.post
  } catch {
    return "Could not reach the server."
  }
}

// Files a moderation report on a post. Returns true on success (including
// the "you already reported this" case, which the server treats as done), or
// a string error to show in the modal.
export async function reportPost(
  postsUrl: string,
  postId: string,
  report: { reason_category: string; explanation?: string },
): Promise<true | string> {
  if (!csrfToken()) return "Not signed in."

  try {
    const response = await fetch(`${postsUrl}/${postId}/reports`, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({ report }),
    })
    const payload = (await response.json()) as { reported?: boolean; error?: string }
    if (!response.ok || !payload.reported) return payload.error ?? "Something went wrong."
    return true
  } catch {
    return "Could not reach the server."
  }
}

// Returns the created comment, or a string error to show inline.
export async function createComment(
  postsUrl: string,
  postId: string,
  comment: { text_contents: string; reply_to_comment_id?: string },
): Promise<CommentRecord | string> {
  if (!csrfToken()) return "Not signed in."

  try {
    const response = await fetch(`${postsUrl}/${postId}/comments`, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({ comment }),
    })
    const payload = (await response.json()) as { comment?: CommentRecord; error?: string }
    if (!response.ok || !payload.comment) return payload.error ?? "Something went wrong."
    return payload.comment
  } catch {
    return "Could not reach the server."
  }
}
